/**
 * RequireAuth — the gate around every in-app route.
 *
 * Calls useMe() (GET /api/v2/me). While it resolves, show a lightweight splash.
 * On success it renders the AppShell (via <Outlet/>). On a 401 the query client's
 * global handler already redirected to /auth/login; here we also guard by
 * bouncing to /login if identity never hydrated, so a deep link to a protected
 * route can't render empty chrome.
 *
 * Backend UNREACHABLE (transport error, or the gateway's 502/503/504) is NOT a reason to
 * send the operator to /login -- signing in again cannot help. Since 2026-10-31 it shows an
 * "unreachable, retry" card instead (decision in lib/authGate).
 */
import { Navigate, Outlet } from "react-router-dom";
import { useMe } from "@/lib/api/hooks";
import { authGateState } from "@/lib/authGate";
import { Button } from "@/components/ui/Button";

export function RequireAuth() {
  const { data, isLoading, error, refetch, isFetching } = useMe();
  const state = authGateState({ isLoading, data, error });

  if (state === "loading") {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-ground">
        <div className="flex flex-col items-center gap-3 text-ink-muted">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-line border-t-brand" />
          <span className="text-sm">Loading session…</span>
        </div>
      </div>
    );
  }

  if (state === "unreachable") {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-ground">
        <div
          role="alert"
          className="flex max-w-sm flex-col items-center gap-3 text-center text-ink-muted"
        >
          <span className="text-sm font-medium text-ink">Controller unreachable</span>
          <span className="text-sm">
            The Meridian controller did not answer. Your session may still be valid; retry
            once it is back.
          </span>
          <Button variant="primary" onClick={() => void refetch()} disabled={isFetching}>
            {isFetching ? "Retrying…" : "Retry"}
          </Button>
        </div>
      </div>
    );
  }

  // 401, or identity never hydrated -> /login. (useMe() only uses mock data when the build
  // runs with mocks enabled -- lib/api/hooks withMock/USE_MOCKS; a real transport error is
  // handled above, it no longer "falls through to mock".)
  if (state === "login") {
    return <Navigate to="/login" replace />;
  }

  return <Outlet />;
}
